import { apiPath } from '../../constants/apiPathConstants';
import { axios, apiRequestOptions, transformHttpResponse, errorRequestHandler } from 'utils';

export const FIGHT_PROTOCOL_LOADED = 'FIGHT_PROTOCOL_LOADED';

/**
 * Every protocol command answers with the WHOLE sheet, and the reducer
 * replaces that one object.
 *
 * The panel therefore never refetches after a score: React re-renders the one
 * card whose data changed, so focus, scroll and any open menu survive a fight
 * being scored — which is the same rule the tatami floor follows, and the
 * reason result entry does not remount here either.
 */
const call = (path, params, body = {}) => dispatch =>
    // `params` is the PATH interpolation and must be passed as `options.params`
    // — handing it in as the whole options object leaves every `{id}` in the
    // template resolving to the string "undefined", which is a 404 rather than
    // an error. See docs/known-issues-and-gotchas.md.
    axios(apiRequestOptions(path, body, { params }))
        .then(transformHttpResponse)
        .then(response => {
            dispatch({ type: FIGHT_PROTOCOL_LOADED, protocol: response.data });
            return response.data;
        })
        .catch(error => {
            // axios rejects on a non-2xx, so transformHttpResponse above never
            // runs and this is the RAW axios error. errorRequestHandler unwraps
            // `.response` itself — see the note there, and the pilot finding
            // that made it necessary.
            dispatch(errorRequestHandler(error));

            // TWO WRITERS, ONE SHEET — the phone and the laptop at one mat
            // (6 Oct 2026). The other device's action landed first and this
            // one was refused whole; show the sheet as it now stands, so the
            // secretary repeats the action against what really happened.
            const response = error && error.response;
            if (response && 409 === response.status && params && params.id
                && response.data && 'errors.protocol.concurrentWrite' === response.data.message) {
                dispatch(call(apiPath.FIGHT_PROTOCOL.GET, { id: params.id })).catch(() => {});
            }

            return Promise.reject(error);
        });

/**
 * Open a sheet. `pair` is omitted for a score TABLE — that is what asks the
 * backend for the whole round rather than one fight.
 */
export const openFightProtocol = (competitionId, categoryId, round, pair) =>
    call(
        apiPath.FIGHT_PROTOCOL.OPEN,
        {},
        undefined === pair || null === pair
            ? { competitionId, categoryId, round }
            : { competitionId, categoryId, round, pair },
    );

export const loadFightProtocol = id => call(apiPath.FIGHT_PROTOCOL.GET, { id });

/** start | pause | resume | finish | cancel | undo | confirm */
export const transitionFightProtocol = (id, transition) =>
    call(apiPath.FIGHT_PROTOCOL.TRANSITION, { id, transition });

export const addFightScore = (id, side, code) =>
    call(apiPath.FIGHT_PROTOCOL.SCORE, { id }, { side, code });

export const addFightPenalty = (id, side, code) =>
    call(apiPath.FIGHT_PROTOCOL.PENALTY, { id }, { side, code });

/**
 * A PENALTY BY THE OFFENCE, not by its name.
 *
 * The mat calls what happened — дзьогай, ате, мубобі — and the backend picks
 * the rung from what the athlete already holds (Правила Розділ VIII §18.9).
 * The step can then be neither skipped nor awarded twice, which picking a
 * code by hand allowed.
 */
export const addFightPenaltyOfKind = (id, side, kind) =>
    call(apiPath.FIGHT_PROTOCOL.PENALTY, { id }, { side, kind });

/** ХІКІВАКЕ — the panel could not separate them (Правила §15). */
export const declareFightDraw = id =>
    call(apiPath.FIGHT_PROTOCOL.DRAW, { id });

/** ЕНЧО-СЕН — one more minute, to the first score (§16). */
export const startEnchoSen = id =>
    call(apiPath.FIGHT_PROTOCOL.ENCHO_SEN, { id });

/** ХАНТЕЙ — the winner by the judges' decision (§14.2, §14.8). */
export const decideByHantei = (id, side) =>
    call(apiPath.FIGHT_PROTOCOL.HANTEI, { id }, { side });

/**
 * The clock. `action` is start | pause | resume | stop.
 *
 * There is no local timer command — pressing start does not start anything in
 * the browser, it asks the server to move its clock and then renders whatever
 * came back.
 */
export const setFightTimer = (id, action) =>
    call(apiPath.FIGHT_PROTOCOL.TIMER, { id, action });

/**
 * FUKUGO — name the winner of this fight.
 *
 * The bracket is untouched until the protocol is confirmed, so this is a safe,
 * correctable operational action rather than a result.
 */
export const selectFukugoWinner = (id, participantId) =>
    call(apiPath.FIGHT_PROTOCOL.FUKUGO_WINNER, { id }, { participantId });

/**
 * MSAC Б-5 / Б-6 — all five judges' scores for one athlete.
 *
 * Five at once: the highest and lowest are excluded, so a partial sheet has no
 * defined result and the backend refuses one.
 */
export const recordMsacJudgeScores = (id, participantId, scores, ftkLevel) =>
    call(apiPath.FIGHT_PROTOCOL.MSAC_JUDGES, { id }, { participantId, scores, ftkLevel });

/** MSAC Б-2/Б-3/Б-4 — one scored technical action. */
export const recordMsacAction = (id, side, action) =>
    call(apiPath.FIGHT_PROTOCOL.MSAC_ACTION, { id }, { side, action });

/** One occurrence of an infraction. The backend applies the ladder. */
/** `ruling` — the chief judge's, for an infraction they decide (розділ XIII п. 9). */
export const recordMsacInfraction = (id, side, infraction, ruling) =>
    call(apiPath.FIGHT_PROTOCOL.MSAC_INFRACTION, { id }, ruling ? { side, infraction, ruling } : { side, infraction });

/** The arbiter's ruling on how clearly the bout was won (розділ XIII §1 п. 4). */
export const recordMsacArbiterClass = (id, victoryClass) =>
    call(apiPath.FIGHT_PROTOCOL.MSAC_ARBITER_CLASS, { id }, { victoryClass });

/** The two side judges' totals, entered when the bout ends. */
export const recordMsacJudgeTotals = (id, totals) =>
    call(apiPath.FIGHT_PROTOCOL.MSAC_JUDGE_TOTALS, { id }, { totals });

/** The bout ended outright — submission, knockout, disqualification. */
export const declareMsacCleanWin = (id, side, reason) =>
    call(apiPath.FIGHT_PROTOCOL.MSAC_CLEAN_WIN, { id }, { side, reason });

/**
 * MSAC bouts — one athlete's stopwatch: the doctor, the equipment, lateness
 * (Розділ X; 2 Nov 2026). The doctor and the equipment pause the bout's clock.
 */
export const startMsacTimeout = (id, side, kind) =>
    call(apiPath.FIGHT_PROTOCOL.MSAC_TIMEOUT_START, { id }, { side, kind });

/** Whose — both athletes may be out at once (3 Nov 2026). */
export const stopMsacTimeout = (id, side) =>
    call(apiPath.FIGHT_PROTOCOL.MSAC_TIMEOUT_STOP, { id }, side ? { side } : {});

/** Accept the lateness penalty its minutes reach (Розділ XIII §12). */
export const penalizeMsacLateness = (id, side) =>
    call(apiPath.FIGHT_PROTOCOL.MSAC_LATENESS, { id }, { side });

/**
 * MSAC Б-1 … Б-4 — move the bout to its next part.
 *
 * Round 1 → rest → round 2, as Розділ X orders them. The panel does not say
 * which: it asks for the next one and renders what comes back.
 */
export const advanceMsacPeriod = id =>
    call(apiPath.FIGHT_PROTOCOL.MSAC_PERIOD, { id });

/**
 * Every fight protocol of one category — READ ONLY.
 *
 * The bracket screen's review path: what happened in each fight, and which
 * fights are already decided by a sheet and must not be re-decided by a click.
 */
export const fetchCategoryFightProtocols = (competition, category) => () =>
    // NOT through `call`: that dispatches FIGHT_PROTOCOL_LOADED, whose reducer
    // slot holds the ONE protocol a mat is working on. Putting a list there
    // would replace the sheet under a secretary's hands. This is a read for a
    // review screen, and it answers to its caller.
    axios(apiRequestOptions(
        apiPath.FIGHT_PROTOCOL.FOR_CATEGORY,
        {},
        { params: { competition, category } },
    ))
        .then(transformHttpResponse)
        .then(response => response.data);

/**
 * The fight a mat runs now and next for one category — `{ tatami, current,
 * next }` or null — for the bracket's «NEXT» (26 Oct 2026). A read for its
 * caller, like fetchCategoryFightProtocols.
 */
export const fetchCategoryMatFights = (competition, category) => () =>
    axios(apiRequestOptions(
        apiPath.FIGHT_PROTOCOL.MAT_FIGHTS,
        {},
        { params: { competition, category } },
    ))
        .then(transformHttpResponse)
        .then(response => response.data);

/**
 * ONE sheet, for a review — the «i» of a bout already fought (26 Oct 2026).
 * Not through `call`, for the same reason as fetchCategoryFightProtocols: the
 * store's protocol slot belongs to the sheet a mat is working on.
 */
export const fetchFightProtocolForReview = id => () =>
    axios(apiRequestOptions(apiPath.FIGHT_PROTOCOL.GET, {}, { params: { id } }))
        .then(transformHttpResponse)
        .then(response => response.data);

/** KATA — one athlete's score, or an explicit DNS. */
export const recordKataScore = (id, participantId, score, dns = false) =>
    call(apiPath.FIGHT_PROTOCOL.KATA_SCORE, { id }, { participantId, score, dns });

/**
 * KATA — what the five judges showed.
 *
 * The TOTAL is not sent. The rule that turns five scores into one — drop the
 * highest and the lowest, sum the other three — belongs to the engine, which
 * also refuses a judge outside the sport's range. A client that summed them
 * here would be a second implementation of a competition rule, and the first
 * one to disagree would do so silently.
 */
export const recordKataJudgeScores = (id, participantId, judgeScores) =>
    call(apiPath.FIGHT_PROTOCOL.KATA_SCORE, { id }, { participantId, judgeScores, dns: false });

/**
 * БУНКАЙ — the second half of a TEAM kata performance.
 *
 * Same panel, same exclusion, same range; the engine sums it and adds it to the
 * kata half, so the total is never computed here either. Individual categories
 * refuse the command outright — whether a category performs bunkai is the
 * engine's answer, reported to the sheet as `view.bunkai`.
 */
export const recordKataBunkaiScores = (id, participantId, judgeScores) =>
    call(apiPath.FIGHT_PROTOCOL.KATA_BUNKAI, { id }, { participantId, judgeScores });

/**
 * CALL ONE ATHLETE OUT — kata is performed one at a time.
 *
 * `null` clears the mat between performances. The public display shows this
 * athlete rather than the whole table, which is what the hall is watching.
 */
export const callKataPerformer = (id, participantId) =>
    call(apiPath.FIGHT_PROTOCOL.KATA_PERFORMER, { id }, { participantId });

/**
 * The score range the judging panel announced for this round (Розділ X §2.2).
 *
 * Pass nulls to clear it — the round then scores on the plain ten-point scale.
 */
export const setKataScoreRange = (id, min, max) =>
    call(apiPath.FIGHT_PROTOCOL.KATA_RANGE, { id }, { min, max });

/** Which kata this athlete is performing (Розділ X §2.9). */
export const setKataName = (id, participantId, kata) =>
    call(apiPath.FIGHT_PROTOCOL.KATA_NAME, { id }, { participantId, kata });

/**
 * HOW MANY JUDGES sit for this category — 5, or 3 when a mat cannot seat five.
 *
 * The backend writes it to the CATEGORY (every round of it agrees, §2.3) and
 * refuses the change once anybody has been scored.
 */
export const setKataJudgeCount = (id, judgeCount) =>
    call(apiPath.FIGHT_PROTOCOL.KATA_JUDGES, { id }, { judgeCount });

/** FLAGS — who won, recorded at the mat. */
export const selectFlagWinner = (id, participantId) =>
    call(apiPath.FIGHT_PROTOCOL.FLAG_WINNER, { id }, { participantId });

/** KATA — the judge's ordering inside a group of equal scores. */
export const recordKataTieBreak = (id, participantId, order) =>
    call(apiPath.FIGHT_PROTOCOL.KATA_TIE_BREAK, { id }, { participantId, order });
